import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaHome, FaClock, FaSignOutAlt, FaMicrophone, FaCog, FaTheaterMasks, FaLock, FaComments, FaMobile } from 'react-icons/fa';
import { AppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';

// TEST: Import both Button and StatusIndicator
import { Button, StatusIndicator } from '../design-system/components';

import './Header.css';

const Header = ({ systemStatus }) => {
  const { isConnected } = useContext(AppContext);
  const { user, logout } = useAuth();
  const { isFeatureEnabled, loading: featuresLoading } = useFeatures();
  const location = useLocation();
  
  const allTabs = [
    { id: 'microphones', label: 'Microphones', icon: FaMicrophone, path: '/microphones', feature: 'microphones' },
    { id: 'devices', label: 'Devices', icon: FaMobile, path: '/devices', feature: 'devices' },
    { id: 'smartlocks', label: 'Smart Locks', icon: FaLock, path: '/smartlocks', feature: 'smartLocks' },
    { id: 'scenes', label: 'Scenes', icon: FaTheaterMasks, path: '/scenes', feature: 'scenes' },
    { id: 'automations', label: 'Automations', icon: FaCog, path: '/automations', feature: 'automations' },
    { id: 'conversation', label: 'Conversation', icon: FaComments, path: '/conversation', feature: 'conversations' },
    { id: 'settings', label: 'Settings', icon: FaCog, path: '/settings', feature: 'systemSettings' }
  ];
  
  // Filter tabs based on feature configuration
  // Only show tabs if features are loaded to prevent showing all tabs initially
  const tabs = featuresLoading ? [] : allTabs.filter(tab => 
    !tab.feature || isFeatureEnabled(tab.feature)
  );
  
  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };
  
  return (
    <header className="app-header text-white px-3 py-3 md:px-5 md:py-4 shadow-lg border-b border-white/5">
      {/* Header Top Section - Mobile optimized layout */}
      <div className="flex flex-col gap-3 mb-3 md:flex-row md:justify-between md:items-center md:gap-4">
        
        {/* Logo Section */}
        <div className="flex justify-center items-center gap-2.5 md:justify-start">
          <div className="bg-white text-primary-500 w-8 h-8 md:w-10 md:h-10 rounded-xl flex justify-center items-center text-lg md:text-xl font-bold shadow-sm">
            <FaHome />
          </div>
          <h1 className="text-lg md:text-xl font-semibold m-0 whitespace-nowrap">Mirfa Home</h1>
        </div>
        
        {/* System Status & User Info - Better mobile layout */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 md:justify-end">
          
          {/* Connection Status */}
          <StatusIndicator
            status={isConnected ? 'online' : 'offline'}
            text={isConnected ? 'Connected' : 'Disconnected'}
            size="sm"
          />
          
          {/* Latency */}
          {systemStatus.latency && (
            <StatusIndicator
              status="unknown"
              text={`${systemStatus.latency}ms`}
              icon={<FaClock />}
              showDot={false}
              size="sm"
            />
          )}
          
          {/* User Info */}
          <div className="flex items-center gap-2 bg-white/10 px-2.5 py-1.5 rounded-xl text-xs md:text-sm">
            <span className="text-white/80 font-normal hidden md:inline">
              Welcome, {user?.username}
            </span>
            
            <Button
              variant="secondary"
              size="sm"
              onClick={() => logout()}
              title="Sign Out"
              className="!min-h-8 !px-2 md:!px-3"
            >
              <FaSignOutAlt />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Navigation Tabs - Improved spacing and touch targets */}
      <nav className="flex gap-1 md:gap-2 overflow-x-auto pb-1 w-full">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          const active = isActive(tab.path);
          return (
            <Link 
              key={tab.id}
              to={tab.path}
              className={`
                flex-1 md:flex-none px-2 md:px-4 py-2.5 rounded-lg md:rounded-2xl text-sm md:text-base
                flex items-center justify-center md:justify-start gap-1.5 md:gap-2 
                whitespace-nowrap cursor-pointer transition-all duration-200
                min-h-11 md:min-w-max
                ${active 
                  ? 'bg-white/20 text-white font-semibold' 
                  : 'text-white/75 hover:bg-white/10 hover:text-white'
                }
              `}
            >
              <IconComponent className="text-base md:text-base flex-shrink-0" />
              <span className="hidden md:inline truncate">{tab.label}</span>
            </Link>
          );
        })}
      </nav>
    </header>
  );
};

export default Header;

